const STORAGE_KEY = 'cc_app_tabs';
const ACTIVE_TAB_KEY = 'cc_active_tab';
const MAX_TABS = 20;

let _saveTimer = null;
function debouncedSaveTabsToStorage(tabs) {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => saveTabsToStorage(tabs), 100);
}

let _saveActiveTimer = null;
function debouncedSaveActiveTabToStorage(path) {
  if (_saveActiveTimer) clearTimeout(_saveActiveTimer);
  _saveActiveTimer = setTimeout(() => saveActiveTabToStorage(path), 100);
}

export function getRouteLabel(route) {
  if (!route) return 'Página';

  const label = route.meta && route.meta.title;
  if (label) {
    if (route.params && Object.keys(route.params).length > 0) {
      const paramValues = Object.values(route.params).filter(Boolean);
      const lastParam = paramValues[paramValues.length - 1];
      if (lastParam) {
        const paramDisplay = decodeURIComponent(lastParam).replace(/-/g, ' ');
        return `${label} — ${paramDisplay}`;
      }
    }
    return label;
  }

  // Fallback: derive title from URL path
  const segments = (route.fullPath || route.path || '').split('?')[0].split('/').filter(Boolean);
  if (segments.length > 0) {
    return segments[segments.length - 1]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'Página';
}

function loadTabsFromStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function loadActiveTabFromStorage() {
  try {
    return localStorage.getItem(ACTIVE_TAB_KEY) || null;
  } catch {
    return null;
  }
}

function saveTabsToStorage(tabs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // fail silently if storage quota exceeded
  }
}

function saveActiveTabToStorage(path) {
  try {
    if (path) {
      localStorage.setItem(ACTIVE_TAB_KEY, path);
    } else {
      localStorage.removeItem(ACTIVE_TAB_KEY);
    }
  } catch {
    // fail silently
  }
}

// Extract path without query params for tab identity
function normalizeTabPath(route) {
  if (route.path) return route.path;
  return (route.fullPath || '/').split('?')[0];
}

export const tabs = {
  namespaced: true,

  state: {
    tabs: loadTabsFromStorage(),
    activeTabPath: loadActiveTabFromStorage(),
  },

  getters: {
    tabs: (state) => state.tabs,
    activeTabPath: (state) => state.activeTabPath,
    isTabOpen: (state) => (path) => state.tabs.some((t) => t.path === path),
  },

  mutations: {
    ADD_TAB(state, tab) {
      const exists = state.tabs.find((t) => t.path === tab.path);
      if (exists) {
        exists.title = tab.title;
        exists.fullPath = tab.fullPath;
        debouncedSaveTabsToStorage(state.tabs);
        return;
      }
      while (state.tabs.length >= MAX_TABS) {
        const oldestIndex = state.tabs.findIndex((t) => t.path !== state.activeTabPath);
        if (oldestIndex === -1) break;
        state.tabs.splice(oldestIndex, 1);
      }
      state.tabs.push(tab);
      debouncedSaveTabsToStorage(state.tabs);
    },

    REMOVE_TAB(state, path) {
      state.tabs = state.tabs.filter((t) => t.path !== path);
      debouncedSaveTabsToStorage(state.tabs);
    },

    SET_ACTIVE_TAB(state, path) {
      state.activeTabPath = path;
      debouncedSaveActiveTabToStorage(path);
    },

    CLOSE_OTHER_TABS(state, path) {
      state.tabs = state.tabs.filter((t) => t.path === path);
      saveTabsToStorage(state.tabs);
    },

    CLOSE_ALL_TABS(state) {
      state.tabs = [];
      saveTabsToStorage(state.tabs);
    },

    UPDATE_TAB_TITLE(state, { path, title }) {
      const tab = state.tabs.find((t) => t.path === path);
      if (tab) {
        tab.title = title;
        debouncedSaveTabsToStorage(state.tabs);
      }
    },
  },

  actions: {
    openTab({ commit }, route) {
      const path = normalizeTabPath(route);
      const tab = {
        path,
        fullPath: route.fullPath || path,
        title: getRouteLabel(route),
        name: route.name,
      };
      commit('ADD_TAB', tab);
      commit('SET_ACTIVE_TAB', path);
    },

    closeTab({ commit, state }, path) {
      const tabIndex = state.tabs.findIndex((t) => t.path === path);
      const isActive = state.activeTabPath === path;

      commit('REMOVE_TAB', path);

      if (isActive && state.tabs.length > 0) {
        const nextIndex = Math.min(tabIndex, state.tabs.length - 1);
        const nextTab = state.tabs[nextIndex];
        commit('SET_ACTIVE_TAB', nextTab.path);
        return nextTab.fullPath || nextTab.path;
      } else if (isActive) {
        commit('SET_ACTIVE_TAB', null);
      }
      return null;
    },

    closeOtherTabs({ commit }, path) {
      commit('CLOSE_OTHER_TABS', path);
      commit('SET_ACTIVE_TAB', path);
    },

    closeAllTabs({ commit }) {
      commit('CLOSE_ALL_TABS');
      commit('SET_ACTIVE_TAB', null);
    },
  },
};
